<script lang="ts" setup>
import { computed } from 'vue'

import { formatBoolean } from '../../utils/format'

const emit = defineEmits(['update:modelValue'])

const props = defineProps({
  modelValue: {
    type: String,
    required: true,
  },

  name: {
    type: String,
    default: null,
  },

  label: {
    type: String,
    default: null,
  },

  errorLabel: {
    type: [Array, String],
    default: null,
  },

  isDisabled: {
    type: Boolean,
    default: false,
  },

  isRequired: {
    type: Boolean,
    default: false,
  },
})

const value = computed<string>({
  get: (): string => props.modelValue as string,
  set: (val: string): void => emit('update:modelValue', val),
})

const errorLabelList = computed<Array<string>>(() =>
  Array.isArray(props.errorLabel) ? props.errorLabel : [props.errorLabel],
)
</script>

<template>
  <div class="form-check-box">
    <label v-if="label" :for="name">{{ label }}<span>:</span></label>

    <div class="form-check-box-control">
      <input
        v-model.trim="value"
        :class="{ 'has-error': Boolean(errorLabel) }"
        :disabled="isDisabled"
        :required="isRequired"
        :name="name"
        type="checkbox"
      />

      {{ formatBoolean(!!value) }}
    </div>

    <template v-if="errorLabel">
      <small
        v-for="(error, index) in errorLabelList"
        :key="`error-label-${index}`"
        class="error-label"
      >
        {{ error }}
      </small>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';
@import 'src/assets/scss/controls';

$box-shadow: 0 0 (3 * $border-width);

.form-check-box {
  display: flex;
  flex-flow: column nowrap;
  gap: 0.5em;

  .form-check-box-control {
    display: flex;
    flex-flow: row nowrap;
    gap: 0.5em;
  }

  strong {
    margin: 0.125em 0;
  }

  input {
    width: 2em;
    height: 2em;
    background-color: $color-background;
    color: $color-text;
    box-shadow: $box-shadow $color-border;

    &:focus-visible {
      outline: $border-width solid $color-accent;
    }

    &:disabled {
      background-color: $color-background-disabled;
      color: $color-text-disabled;
      cursor: not-allowed;
      user-select: none;
    }

    &.has-error {
      background-color: color.adjust($color-danger, $lightness: 48%);
      color: $color-danger;
      box-shadow: $box-shadow $color-danger;

      &:focus-visible {
        outline-color: $color-danger;
      }
    }
  }

  .error-label {
    color: $color-danger;
  }
}
</style>
